
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function PaarlPage() {
  useEffect(() => {
    document.title = 'Paarl Painters Western Cape | Wine Country Painting Services | Heritage Property Specialists';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Paarl, Western Cape. Expert wine country property painting, heritage restoration, and commercial painting. Serving Paarl and surrounding wine regions.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Paarl painters, wine country painting, heritage restoration Western Cape');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/paarl`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Paarl Painters Western Cape",
      "description": "Professional painting services in Paarl, Western Cape",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/paarl`,
      "areaServed": {
        "@type": "Place",
        "name": "Paarl, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.7369,
          "longitude": 18.9584
        },
        "geoRadius": "30000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Paarl Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/83c53235d3099bc76dfd42982f6fe3b4.jpg"
            alt="Professional painting services in Paarl, Western Cape"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Expert <span className="text-yellow-400">Paarl Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for Paarl's wine country properties. Specialized heritage restoration and expert craftsmanship for vineyards, estates, and historic buildings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Wine Country Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Specialized Wine Country Painting Services in Paarl
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert painting solutions for Paarl's unique wine country environment, from heritage Cape Dutch architecture to modern vineyard facilities and residential properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-2-line',
                title: 'Heritage Restoration',
                description: 'Specialized restoration of historic Cape Dutch buildings, wine cellars, and heritage properties with authentic period techniques.',
                features: ['Cape Dutch architecture', 'Historic wine cellars', 'Period-appropriate colors', 'Conservation techniques']
              },
              {
                icon: 'ri-plant-line',
                title: 'Wine Estate Painting',
                description: 'Professional painting for vineyards, tasting rooms, and wine production facilities with durable, food-safe finishes.',
                features: ['Tasting rooms', 'Wine cellars', 'Production facilities', 'Estate buildings']
              },
              {
                icon: 'ri-home-smile-2-line',
                title: 'Residential Properties',
                description: 'Complete home painting services for Paarl residents, from modern homes to traditional farmhouses.',
                features: ['Modern homes', 'Traditional farmhouses', 'Mountain properties', 'Garden cottages']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Paarl Wine Country Properties Choose Our Expertise
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Heritage Specialization',
                    description: 'Extensive experience with Cape Dutch architecture and historic building restoration using traditional techniques and materials.'
                  },
                  {
                    title: 'Wine Industry Knowledge',
                    description: 'Understanding of wine estate requirements including food-safe finishes and specialized coatings for production areas.'
                  },
                  {
                    title: 'Mountain Climate Expertise',
                    description: 'Knowledge of Paarl\'s unique mountain climate conditions and appropriate protective coatings for varying weather.'
                  },
                  {
                    title: 'Tourism Sensitivity',
                    description: 'Flexible scheduling and discrete service to minimize impact on wine tourism and harvest operations.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Paarl%20wine%20estate%2C%20heritage%20Cape%20Dutch%20building%20restoration%2C%20vineyard%20property%20maintenance%2C%20mountain%20backdrop%20Western%20Cape%2C%20wine%20country%20painting%20services&width=600&height=700&seq=paarl-team-work&orientation=portrait"
                alt="Professional heritage painting in Paarl"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Paarl Wine Country Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our latest wine country painting projects in Paarl, demonstrating our expertise in heritage restoration and estate maintenance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Historic Wine Estate Restoration',
                location: 'Paarl Wine Route',
                description: 'Complete restoration of a 200-year-old Cape Dutch wine estate including cellars and tasting facilities.',
                image: 'https://readdy.ai/api/search-image?query=Historic%20wine%20estate%20restoration%20in%20Paarl%2C%20Cape%20Dutch%20architecture%20painting%2C%20heritage%20wine%20cellar%20restoration%2C%20traditional%20white-washed%20buildings%20with%20mountain%20views&width=400&height=300&seq=paarl-project-1&orientation=landscape'
              },
              {
                title: 'Modern Vineyard Facility',
                location: 'Paarl Valley',
                description: 'Professional painting of a state-of-the-art wine production facility and visitor center.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20vineyard%20facility%20painting%20in%20Paarl%20Valley%2C%20contemporary%20wine%20production%20building%2C%20professional%20commercial%20painting%2C%20wine%20industry%20architecture%20Western%20Cape&width=400&height=300&seq=paarl-project-2&orientation=landscape'
              },
              {
                title: 'Mountain Estate Home',
                location: 'Paarl Mountain',
                description: 'Luxury mountain home painting with panoramic views of the Paarl Valley and vineyards.',
                image: 'https://readdy.ai/api/search-image?query=Mountain%20estate%20home%20painting%20in%20Paarl%2C%20luxury%20residential%20property%20with%20vineyard%20views%2C%20modern%20architecture%20against%20mountain%20backdrop%2C%20high-end%20home%20painting%20Western%20Cape&width=400&height=300&seq=paarl-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Paarl Wine Country Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied wine estate owners and residents in Paarl
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Johann van der Merwe',
                location: 'Paarl Wine Route',
                rating: 5,
                text: 'Exceptional heritage restoration work on our family wine estate. They understood the historical significance and delivered authentic results that honor our legacy.'
              },
              {
                name: 'Claire Dubois',
                location: 'Paarl Valley',
                rating: 5,
                text: 'Professional service for our modern winery facility. They worked around our harvest schedule and used appropriate food-safe finishes. Excellent attention to detail.'
              },
              {
                name: 'Robert Mitchell',
                location: 'Paarl Mountain',
                rating: 5,
                text: 'Outstanding work on our mountain estate home. The quality and durability of the paint has held up beautifully against the mountain weather conditions.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Enhance Your Paarl Wine Country Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for specialized wine country painting consultation and heritage restoration services. Preserve and enhance your Paarl property with expert craftsmanship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
